import { Canvas , FabricImage , FabricText , Path , Group } from 'fabric';
import { useEffect, useRef } from 'react';

function FabricCanvas({ 
    layers,
    selectedLayerId,
    setSelectedLayerId,
    onLayerUpdate,
    selectedSide,
    selectedVariantOption,
    layerConfig,
    showGrid
}) {
    const canvasRef = useRef( null );
    const fabricCanvasRef = useRef( null );
    const selectedLayerIdRef = useRef( null );
    const layersRef = useRef( layers );
    const selectedSideRef = useRef( selectedSide );
    const selectedOptionRef = useRef( selectedVariantOption );
    const layerConfigRef = useRef( layerConfig );
    const suppressSelectionClearRef = useRef( false );
    const debug = false;

    const canvasMaxWidth = 600;
    const canvasMaxHeight = 600;

    async function loadImageOnCanvas( canvas , base64Image , props = {} , isSelected , layerId ) {
        if ( canvas != null ) {
            const existing = canvas.getObjects().find( obj => obj.layerId === layerId );
            if ( existing ) { 
                const currentSrc = existing._src;
                if ( currentSrc !== base64Image ) {
                    suppressSelectionClearRef.current = true;    
                    // Remove the old image
                    canvas.remove( existing );
                    setTimeout(() => {
                        suppressSelectionClearRef.current = false;
                    }, 0);

                    // Add the new image
                    if ( base64Image !== null ) {
                        const image = await FabricImage.fromURL( base64Image );

                        scaleImageToFitCanvas( image, canvasMaxWidth, canvasMaxHeight );
                        image.set( props );
                        image.set({ layerId: layerId });
                        image._src = base64Image;
                        canvas.add( image );

                        if ( isSelected ) {
                           canvas.setActiveObject( image );
                        }
                    }
                } else {
                    existing.set( props );
                    existing.set({ layerId: layerId });
                    existing.setCoords();

                    canvas.requestRenderAll();

                    if ( isSelected ) {
                        canvas.setActiveObject( existing );
                    }
                }
            } else {
                if ( base64Image !== null ) {
                    const image = await FabricImage.fromURL( base64Image );
                    scaleImageToFitCanvas( image, canvasMaxWidth, canvasMaxHeight );
                    image.set( props );
                    image.set( { layerId: layerId } );
                    image._src = base64Image;
                    canvas.add( image );

                    if ( isSelected ) {
                        canvas.setActiveObject( image );
                    }

                    canvas.fire( 'image:loaded' , { target: image } );
                }
            }
        }
    }

    async function loadTextOnCanvas( canvas , isSelected , layerId , currentFieldOptions ) {
        if ( canvas == null ) {
            return;
        }
        
        const currentFabricProps = currentFieldOptions?.field_name_settings?.options?.body?.fabric_props || {};
        const currentText = currentFieldOptions?.field_name_settings?.options?.body?.value || 'Text';
        const currentFontFamily = currentFieldOptions?.font_settings?.options?.font?.value || "Times New Roman";
        const currentFontColor = currentFieldOptions?.font_settings?.options?.color?.value || "#000000";
        const currentFontWeight = currentFieldOptions?.font_settings?.options?.font_weight?.value || "normal";
        const currentFontSize = parseInt( currentFieldOptions?.font_settings?.options?.font_size?.value || "40" );
        const currentFontLineHeight = parseFloat( currentFieldOptions?.font_settings?.options?.line_height?.value || "1.16" );
        const currentLetterSpacingValue = parseFloat( currentFieldOptions?.font_settings?.options?.letter_spacing?.value || "0" );

        const fontFamilyData = currentFieldOptions?.font_settings?.options?.font?.options || [];
        await loadCanvasFonts( fontFamilyData );

        const calculatedCharSpacing = ( currentLetterSpacingValue / currentFontSize ) * 1000;

        const textProps = {
            ...currentFabricProps,
            layerId: layerId,
            fontFamily: currentFontFamily,
            fill: currentFontColor,
            fontWeight: currentFontWeight,
            lineHeight: currentFontLineHeight,
            fontSize: currentFontSize,
            charSpacing: calculatedCharSpacing,
            lockScalingX: true,
            lockScalingY: true,
        };

        const existing = canvas.getObjects().find( obj => obj.layerId === layerId );
        if ( existing ) {
            const updatedFont = existing.fontFamily !== currentFontFamily ||
                existing.fill !== currentFontColor ||
                existing.fontWeight !== currentFontWeight;

            if ( updatedFont ) {
                canvas.remove( existing );
                const text = new FabricText( currentText , textProps );
                canvas.add( text );
                if ( isSelected ) {
                    canvas.setActiveObject( text );
                }
            } else {
                existing.set({ text: currentText , ...textProps } );
                canvas.requestRenderAll();

                if ( isSelected ) {
                    canvas.setActiveObject( existing );
                }
            }
        } else {
            const text = new FabricText( currentText , textProps );
            canvas.add( text );
            if ( isSelected ) {
                canvas.setActiveObject( text );
            }
        }
    }

    async function loadCircleTextOnCanvas( canvas , isSelected, layerId , currentFieldOptions ) {
        if ( canvas == null ) {
            return;
        }
        
        // this is purely visual to show the user where on the circle the gemstone will be
        const gemstoneImagePlaceholder = "https://cdn.shopify.com/s/files/1/0738/3341/4879/files/gemstone-filler.png?v=1750359855";

        // load current fabric props
        const currentFabricProps = currentFieldOptions?.font_settings?.options?.word_count?.fabric_props || {};
        
        // load words
        const words = [];
        const wordCountAsString = currentFieldOptions?.font_settings?.options?.word_count?.value || "1";
        const wordCountAsInt = parseInt( wordCountAsString );
        for ( let i = 1; i <= wordCountAsInt; i++ ) {
            const key = `word_${i}`;
            const currentWordValue = currentFieldOptions?.font_settings?.options[ key ]?.value ?? "";
            words.push( currentWordValue );
        }

        // load font details
        const currentFontFamily = currentFieldOptions?.font_settings?.options?.font?.value || "Times New Roman";
        const currentFontColor = currentFieldOptions?.font_settings?.options?.color?.value || "#000000";
        const currentFontWeight = currentFieldOptions?.font_settings?.options?.font_weight?.value || "normal";
        const currentFontSize = parseInt( currentFieldOptions?.font_settings?.options?.font_size?.value || "40" );
        const currentFontLineHeight = parseFloat( currentFieldOptions?.font_settings?.options?.line_height?.value || "1.16" );
        const currentLetterSpacingValue = parseFloat( currentFieldOptions?.font_settings?.options?.letter_spacing?.value || "0" );
        const calculatedCharSpacing = ( currentLetterSpacingValue / currentFontSize ) * 1000;
        const fontFamilyData = currentFieldOptions?.font_settings?.options?.font?.options || [];
        await loadCanvasFonts( fontFamilyData );

        // https://developer.mozilla.org/en-US/docs/Web/SVG/Tutorials/SVG_from_scratch/Paths
        // An uppercase letter specifies absolute coordinates on the page, and a lowercase letter specifies relative coordinate
        // M | m = Move to command
        // M 100 , 100 = Move to position ( 100 , 100 ) [ x , y ]
        
        // A = Arc of a circle
        // EXAMPLE CALL = rx ry x-axis-rotation large-arc-flag sweep-flag x y
        // rx = x radius
        // ry = y radius 
        // x-axis-rotation = 0 = no rotation, -45, spin to the left by 45 deg
        // large-arc-flag = It determines if the arc should be greater than or less than 180 degrees; 0 = <180 | 1 = > 180 | in other words, the direction
        // sweep-flag = 0 = text goes counter clock | 1 = text goes clockwise
        // x = end position of stroke in x 
        // y = end position of stroke in y

        // understanding circle math
            // degrees -> radians conversion https://www.khanacademy.org/math/algebra2/x2ec2f6f830c9fb89:trig/x2ec2f6f830c9fb89:radians/v/radian-and-degree-conversion-practice
            // arc length calculation -> https://www.khanacademy.org/math/geometry/hs-geo-circles/hs-geo-arc-length-deg/v/length-of-an-arc-that-subtends-a-central-angle
            // parametric equations ( points on a circle ) -> https://www.youtube.com/watch?v=IReD6c_njOY 

        // load circle details
        const canvasWidth = canvas.getWidth();
        const canvasHeight = canvas.getHeight();
        const radiusAsString = currentFieldOptions?.font_settings?.options?.radius?.value || "100";
        const radius = parseInt( radiusAsString );
        const arcCount = words.length;
        const anglePerArc = arcCount === 1 ? 359.9 : 360 / arcCount;        // when one arc, we need to define the endpoint of the arc as slightly less to allow for editor rotation. A full circle as arc wont let us rotate
        const cx = canvasWidth / 2;                                         // center of the canvas x
        const cy = canvasHeight / 2;                                        // center of the canvas y
        const groupCenterX = cx - radius;                                   // the 300 , 300 = top left corner of the group minus the distance to the center of the group to move to true center of canvas
        const groupCenterY = cy - radius;
        const startingAngleOffsetAsString = currentFieldOptions?.font_settings?.options?.start_angle?.value || "0";
        const startingAngleOffset = parseInt( startingAngleOffsetAsString );   // 0 deg starts at right side of circle. location and goes clockwise
        const debugColors = [ 'red' , 'green' , 'blue' , 'orange' , 'purple' , 'pink' ];
        let debugColorIndex = 0;

        const groupItems = [];
        for ( let i = 0; i < arcCount; i++ ) {
            const xAxisRotation = 0;
            const sweepFlag = 1;
            const largeArcFlag = anglePerArc > 180 ? 1 : 0;

            const startAngleDeg = i * anglePerArc + startingAngleOffset;
            const endAngleDeg = startAngleDeg + anglePerArc;

            const startRad = ( Math.PI / 180 ) * startAngleDeg;
            const endRad = ( Math.PI / 180 ) * endAngleDeg;

            const x1 = cx + radius * Math.cos( startRad );
            let y1 = cy + radius * Math.sin( startRad );

            const x2 = cx + radius * Math.cos( endRad );
            let y2 = cy + radius * Math.sin( endRad );
            
            if ( y1 == y2 ) {
                y2 = y1 - 0.001;
            }
            
            const pathData = `
                M ${x1} ${y1}
                A ${radius} ${radius} ${xAxisRotation} ${largeArcFlag} ${sweepFlag} ${x2} ${y2}
            `;

            let stroke = 'transparent';
            if ( debug ) {
                stroke = debugColors[ debugColorIndex % debugColors.length ]
                debugColorIndex++;
            }

            const pathProps = {
                stroke: stroke,
                fill: '',
                visible: true,
                originX: 'center',
                originY: 'center',
            };

            const path = new Path( pathData.trim() , pathProps );
            const bounds = path.getBoundingRect();

            if ( words[ i ] != "{{GEMSTONE}}" ) {
                const textProps = {
                    layerId: layerId,
                    fontFamily: currentFontFamily,
                    fill: currentFontColor,
                    fontWeight: currentFontWeight,
                    lineHeight: currentFontLineHeight,
                    fontSize: currentFontSize,
                    charSpacing: calculatedCharSpacing,
                    textAlign: 'center',
                    path: path,
                    left: bounds.left,
                    top: bounds.top,
                    originX: 'left',
                    originY: 'top',
                    pathAlign: 'center', // aligns to the center of the arc drawn
                };
            
                const text = new FabricText( words[ i ] , textProps );
                groupItems.push( text );
            }
            else if ( words[ i ] == "{{GEMSTONE}}" ) {
                const image = await FabricImage.fromURL( gemstoneImagePlaceholder );
                const gemstoneWidth = 30;
                const gemstoneHeight = 30;
                scaleImageToFitCanvas( image, gemstoneWidth, gemstoneHeight );

                const midAngleDeg = startAngleDeg + anglePerArc / 2;
                const midAngleRad = (Math.PI / 180) * midAngleDeg;

                const midX = cx + radius * Math.cos(midAngleRad);
                const midY = cy + radius * Math.sin(midAngleRad);

                image.set({
                    layerId: layerId,
                    originX: 'center',
                    originY: 'center',
                    left: midX,
                    top: midY,
                });

                groupItems.push( image );
            }
        }

        const existingGroupLayers = canvas.getObjects().filter(obj => obj.layerId === layerId);
        if ( existingGroupLayers.length > 0 ) {
            setTimeout(( ) => {
                for ( let i = 0; i < existingGroupLayers.length; i++ ) {
                    canvas.remove( existingGroupLayers[i] );
                }
                
                const group = new Group( groupItems , {
                    layerId: layerId,
                    lockScalingX: true,
                    lockScalingY: true,
                    lockSkewingX: true,
                    lockSkewingY: true,
                    lockRotation: true,
                    lockScalingFlip: true,
                    ...currentFabricProps
                });

                canvas.add( group );

                if ( isSelected ) {
                    canvas.setActiveObject( group );
                }

                canvas.requestRenderAll();
            } , 0 );
        } else {
            // add it for the first time
            // setTimeout adds the layer on the very next UI tick
            setTimeout(( ) => {
                const group = new Group( groupItems , {
                    layerId: layerId,
                    left: groupCenterX,
                    top: groupCenterY,
                    lockScalingX: true,
                    lockScalingY: true,
                    lockSkewingX: true,
                    lockSkewingY: true,
                    lockRotation: true,
                    lockScalingFlip: true
                });

                canvas.add( group );

                if ( isSelected ) {
                    canvas.setActiveObject( group );
                }

                canvas.requestRenderAll();
            } , 0 );
        }
    }

    async function loadCanvasFonts( fontOptions ) {
        for ( let i = 0; i < fontOptions?.length; i++ ) {
            const fontName = fontOptions[i]?.linked_font_data?.font_name;
            const fontStyle = fontOptions[i]?.linked_font_data?.font_style || 'normal';
            const fontWeight = fontOptions[i]?.linked_font_data?.font_weight || 'normal';
            
            const fontProps = {
                style: fontStyle,
                weight: fontWeight
            };

            const fontFiles = fontOptions[i]?.linked_font_data?.files || [];
            for ( let j = 0; j < fontFiles.length; j++ ) {
                const fontUrl = fontFiles[j]?.shopify_cdn_url;
                const fontFaceUrl = `url(${fontUrl})`;

                try {

                    const currentFontFace = new FontFace( fontName, fontFaceUrl , fontProps );
                    await currentFontFace.load();
                    document.fonts.add( currentFontFace );
                    // console.log(`Loaded font "${fontName}" from ${fontUrl}`);
                } catch ( error ) {
                    console.log( error );
                    console.warn( 'Failed to load font file: ' , fontUrl );
                }
            }
        }
    }

    function drawGridOverlay( canvas, { size = 25, color = '#F7F7F7' } = {} ) {
        if (!canvas) return;

        if (canvas.__gridOverlayHandler) {
            canvas.off('after:render', canvas.__gridOverlayHandler);
            canvas.__gridOverlayHandler = null;
        }

        if (!showGrid) {
            const ctx = canvas.getSelectionContext();
            ctx.clearRect(0, 0, canvas.getWidth(), canvas.getHeight());
            canvas.requestRenderAll();
            return;
        }

        const renderGrid = () => {
            const ctx = canvas.getSelectionContext();
            const width = canvas.getWidth();
            const height = canvas.getHeight();

            ctx.clearRect(0, 0, width, height);

            ctx.save();
            ctx.strokeStyle = color;
            ctx.lineWidth = 1;
            ctx.beginPath();

            for (let x = 0; x <= width; x += size) {
                ctx.moveTo(x, 0);
                ctx.lineTo(x, height);
            }

            for (let y = 0; y <= height; y += size) {
                ctx.moveTo(0, y);
                ctx.lineTo(width, y);
            }

            ctx.stroke();
            ctx.restore();
        };

        canvas.__gridOverlayHandler = renderGrid;
        canvas.on('after:render', canvas.__gridOverlayHandler);
        canvas.requestRenderAll();
    }

    function reorderCanvasLayers( canvas ) {
        const canvasObjects = canvas.getObjects();
        const objMap = new Map( canvasObjects.map( canvasObject => [ canvasObject.layerId, canvasObject ] ));
        canvas._objects = layers.slice().reverse().map( layer => objMap.get( layer.id ) ).filter( obj => obj !== undefined );
        
        return;
    }

    function scaleImageToFitCanvas( image, maxWidth, maxHeight ) {
        const { width, height } = image;
        if ( width > maxWidth || height > maxHeight ) {
            const widthRatio = maxWidth / width;
            const heightRatio = maxHeight / height;
            const scale = Math.min( widthRatio, heightRatio );
            image.scale( scale );
        } else {
            image.scale(1);
        }

        return;
    }

    // DO NOT PUT LAYER UPDATES HERE. USE FOR CANVAS EVENTS / INIT
    useEffect( () => {
        // load the canvas defaults
        const canvasOptions = {
            backgroundColor: '#ffffff',
            width: canvasMaxWidth,
            height: canvasMaxHeight
        };
        const canvas = new Canvas( canvasRef.current , canvasOptions );
        canvas.preserveObjectStacking = true;
        fabricCanvasRef.current = canvas;

        function handleModified( e ) {
            // console.log( 'modified layer in some way in canvis' );
            const obj = e.target;
            const layersClone = structuredClone( layersRef.current );
            for ( let i = 0; i < layersClone.length; i++ ) {
                if ( obj.layerId === layersClone[i].id ) {
                    const imageField = layersClone[i]?.field_options?.upload_image?.options?.image;
                    const textField = layersClone[i]?.field_options?.field_name_settings?.options?.body;    // dump all text props here
                    const circleField = layersClone[i]?.field_options?.font_settings?.options?.word_count;

                    if ( imageField != null ) {
                        imageField.fabric_props = {
                            left: obj.left,
                            top: obj.top,
                            angle: obj.angle,
                            scaleX: obj.scaleX,
                            scaleY: obj.scaleY
                        }
                    }

                    if ( textField != null ) {
                        textField.fabric_props = {
                            left: obj.left,
                            top: obj.top,
                            angle: obj.angle,
                            scaleX: obj.scaleX,
                            scaleY: obj.scaleY
                        };
                    }

                    if ( circleField != null ) {
                        circleField.fabric_props = {
                            left: obj.left,
                            top: obj.top,
                            angle: obj.angle,
                            scaleX: obj.scaleX,
                            scaleY: obj.scaleY
                        };
                    }

                    if ( onLayerUpdate ) {
                        onLayerUpdate(
                            layersClone,
                            selectedSideRef.current,
                            selectedOptionRef.current,
                            layerConfigRef.current
                        );
                    }
                    
                    selectedLayerIdRef.current = layersClone[i].id;
                    break;
                }
            }
        }

        function handleSelectionCreated( e ) {
            // console.log( 'clicked something' );
            if ( !e.selected || e.selected.length === 0 ) {
                return; 
            }

            const obj = e.selected[0];
            const layersClone = structuredClone( layersRef.current );
            for ( let i = 0; i < layersClone.length; i++ ) {
                if ( obj.layerId === layersClone[i].id ) {
                    selectedLayerIdRef.current = layersClone[i].id;
                    setSelectedLayerId( layersClone[i].id );
                    break;
                }
            }
        }

        function handleSelectionUpdated( e ) {
            // console.log( 'clicked new layer in canvas' );
            if ( !e.selected || e.selected.length === 0 ) {
                return; 
            }

            const obj = e.selected[0];
            const layersClone = structuredClone( layersRef.current );
            for ( let i = 0; i < layersClone.length; i++ ) {
                if ( obj.layerId === layersClone[i].id ) {
                    console.log( 'element in canvas!' );
                    selectedLayerIdRef.current = layersClone[i].id;
                    setSelectedLayerId( layersClone[i].id );
                    break;
                }
            }
        }

        function clearSelection( e ) {
            if ( suppressSelectionClearRef.current ) {
                // console.log('selection:cleared ignored due to suppression');
                return;
            }

            // console.log('clicked outside all layers');
            selectedLayerIdRef.current = -1;
            setSelectedLayerId( -1 );
        }
        
        canvas.on( 'object:modified', handleModified );
        canvas.on( 'selection:updated', handleSelectionUpdated );
        canvas.on( 'selection:created', handleSelectionCreated );
        canvas.on( 'selection:cleared', clearSelection );
        canvas.on( 'image:loaded' , handleModified );

        // handle cleanup
        return () => {
            canvas.off( 'object:modified', handleModified );
            canvas.off( 'selection:updated', handleSelectionUpdated );
            canvas.off( 'selection:created', handleSelectionCreated );
            canvas.off( 'selection:cleared', clearSelection );
            canvas.dispose();
            fabricCanvasRef.current = null;
        };
    } , [] );

    // USE ONLY WHEN DEALING WITH A SELECTED LAYER CHANGE
    useEffect( () => {
        // console.log( 'selected layer changed' );
        if ( selectedLayerId == -1 ) {
            return;
        }

        const canvas = fabricCanvasRef.current;
        const objects = canvas.getObjects();

        for ( let i = 0; i < objects.length; i++ ) {
            const layerId = objects[i].layerId;

            if ( layerId === selectedLayerId ) {
                selectedLayerIdRef.current = selectedLayerId;
                canvas.setActiveObject( objects[i] );
                canvas.requestRenderAll();

                break;
            }
        }
    } , [ selectedLayerId ] );

    // USE ONLY WHEN DEALING WITH OVERALL LAYER UPDATES.
    useEffect( () => {
        // console.log( layerConfig );
        // console.log( 'refresh layers' );
        // any canvas core logic
        async function loadLayers() {
            const canvas = fabricCanvasRef.current;
            if (!canvas) return;

            // cleanup old layers
            const currentCanvasLayers = canvas.getObjects();
            for ( let i = 0; i < currentCanvasLayers.length; i++ ) {
                const objLayerId = currentCanvasLayers[i].layerId;

                let found = false;
                for ( let j = 0; j < layers.length; j++ ) {
                    const layerIdToCheck = layers[j].id;
                    if ( objLayerId === layerIdToCheck ) {
                        found = true;
                    }
                }

                if ( !found ) {
                    canvas.remove( currentCanvasLayers[i] );
                }
            }

            for ( let i = 0; i < layers.length; i++ ) {
                const type = layers[i].type;
                const selected = layers[i]?.id === selectedLayerIdRef.current;
                const layer = layers[i];

                if ( type === 'image' ) {
                    const imageValue = layer?.field_options?.upload_image?.options?.image?.value;
                    const imageProps = layer?.field_options?.upload_image?.options?.image?.fabric_props;
                    await loadImageOnCanvas( canvas , imageValue , imageProps , selected , layer?.id );
                } else if ( type === 'text' ) {
                    await loadTextOnCanvas( canvas , selected , layer?.id , layer?.field_options );
                } else if ( type === 'gemstones' ) {
                    const imageValue = layer?.field_options?.upload_image?.options?.image?.value;
                    const imageProps = layer?.field_options?.upload_image?.options?.image?.fabric_props;
                    await loadImageOnCanvas( canvas , imageValue , imageProps , selected , layer?.id );
                } else if ( type === 'circletext' ) {
                    await loadCircleTextOnCanvas( 
                        canvas, 
                        selected, 
                        layer?.id, 
                        layer?.field_options
                    );
                }
            }

            reorderCanvasLayers( canvas );
            
            // UI extensions to canvas
            drawGridOverlay( canvas , { size: 25 , color: "#d9d9d9"});
            canvas.requestRenderAll();
        }

        // run layer loading + event listners
        layersRef.current = layers;
        selectedSideRef.current = selectedSide;
        selectedOptionRef.current = selectedVariantOption;
        layerConfigRef.current = layerConfig;
        loadLayers();
    } , [ layers , layerConfig ] );

    useEffect( () => {
        async function reloadUIAddons() {
            const canvas = fabricCanvasRef.current;
            if (!canvas) return;
            drawGridOverlay( canvas , { size: 25 , color: "#d9d9d9"});
        }

        reloadUIAddons();
    } , [ showGrid ] );

    return (
        <canvas
            id="customization-canvas" 
            ref={canvasRef} 
        ></canvas>
    );
}

export default FabricCanvas;